import { ChangeEvent, useState } from "react";
import { Plus, Search, Building2, Phone, Mail, MapPin, Globe, Edit2, Trash2, Users, Download, Check, ChevronsUpDown, Loader2, Handshake, Briefcase, AlertCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { formatPhoneInputValue } from "@/lib/phone";
import { exportToCSV } from "@/lib/exportUtils";
import { AddressFields } from "@/components/forms/AddressFields";
import {
  CompanyContactsDialog,
  CompanyContactsFields,
  useCompanyContactsForm,
  type CompanyTarget,
} from "./CompanyContactsDialog";
import { composeAddressLine, emptyAddressFields, normalizeAddressFields } from "@/lib/address";

interface Contact {
  id: number;
  type: 'carrier' | 'ga' | 'vendor';
  name: string;
  contactName?: string;
  email: string;
  phone?: string;
  website?: string;
  address?: string;
  description?: string;
  category?: string;
  contactType?: 'sales' | 'service';
  carrierId?: number | null;
  carrier?: { id: number; name: string; types: string[] };
  // The wholesaler this person works for. Several contacts share one, the same
  // way several contacts share a carrierId, so the RFP can email all of them.
  generalAgencyId?: number | null;
  generalAgency?: { id: number; name: string } | null;
}

interface GeneralAgency {
  id: number;
  name: string;
  isActive?: boolean | null;
  /** Carriers it can place business with. Empty means no RFP can go through it. */
  carrierIds?: number[];
}

const emptyContactForm = {
  name: "",
  contactName: "",
  email: "",
  phone: "",
  website: "",
  address: "",
  ...emptyAddressFields(),
  description: "",
  category: "",
  type: "carrier",
  contactType: "sales",
  // Typed, or react-hook-form infers `undefined` and reset() with a real id
  // fails to typecheck.
  carrierId: undefined as number | undefined,
};

const CONTACT_TYPE_LABELS: Record<string, string> = {
  carrier: "Insurance Carrier",
  ga: "General Agent (GA)",
  vendor: "Service Vendor",
};

// Matches each type's summary tile, so a card can be placed without reading it.
const CONTACT_TYPE_ICONS: Record<string, typeof Building2> = {
  carrier: Building2,
  ga: Handshake,
  vendor: Briefcase,
};

const CONTACT_TYPE_TONES: Record<string, string> = {
  carrier: "bg-purple-100 dark:bg-purple-900 text-purple-600",
  ga: "bg-cyan-100 dark:bg-cyan-900 text-cyan-600",
  vendor: "bg-orange-100 dark:bg-orange-900 text-orange-600",
};

export default function Directory() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [carrierPopoverOpen, setCarrierPopoverOpen] = useState(false);
  // Carriers and GAs are edited a company at a time, with all their contacts.
  const [companyTarget, setCompanyTarget] = useState<CompanyTarget | null>(null);
  // Rows to seed a NEW company with, to reattach someone whose firm was deleted.
  const [adoptedMembers, setAdoptedMembers] = useState<Contact[]>([]);
  const queryClient = useQueryClient();

  const { data: contacts = [], isLoading } = useQuery<Contact[]>({
    queryKey: ["/api/directory-contacts"],
  });

  const { data: carriers = [] } = useQuery<any[]>({
    queryKey: ["/api/carriers"],
  });

  const { data: generalAgencies = [] } = useQuery<GeneralAgency[]>({
    queryKey: ["/api/general-agencies"],
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/directory-contacts", data).then(r=>r.json()),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/directory-contacts"]}); setIsDialogOpen(false); form.reset(); }
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiRequest("PUT", `/api/directory-contacts/${id}`, data).then(r=>r.json()),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/directory-contacts"]}); setIsDialogOpen(false); setEditingContact(null); form.reset(); }
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/directory-contacts/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/directory-contacts"]})
  });

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch =
      contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.category?.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesTab = false;
    switch (activeTab) {
      case 'all': matchesTab = true; break;
      case 'carrier':
      case 'ga':
      case 'vendor': matchesTab = contact.type === activeTab; break;
      case 'sales': matchesTab = contact.contactType === 'sales'; break;
      case 'service': matchesTab = contact.contactType === 'service'; break;
      default: matchesTab = true;
    }

    return matchesSearch && matchesTab;
  });

  const vendorCount = contacts.filter(c=>c.type==='vendor').length;
  // Firms, not people: the tab lists firms, and two contacts at one GA is one
  // GA. The other tabs count contacts because that is what they list.
  const gaCount = generalAgencies.filter(ga => ga.isActive !== false).length;
  const totalContacts = contacts.length;

  // Named so handleAddContact can reset back to it: inline defaults apply once.
  const form = useForm({ defaultValues: emptyContactForm });

  // Only carriers somebody is on file for. The shared list has 40+ rows, most
  // with nobody, which would bury the few that matter.
  const carriersWithContacts = (carriers as ContactCompany[]).filter((carrier) =>
    contacts.some((contact) => contact.type === "carrier" && contact.carrierId === carrier.id),
  );

  // Creating only. Editing a GA goes through the GA tab, which has its people.
  const isGeneralAgencyMode = !editingContact && form.watch("type") === "ga";
  const generalAgencyForm = useCompanyContactsForm({
    target: { kind: "ga", generalAgency: null },
    members: [],
    allContacts: contacts.filter((contact) => contact.type === "ga"),
    active: isDialogOpen && isGeneralAgencyMode,
    onSaved: () => setIsDialogOpen(false),
  });

  // Must clear both: a stale editingContact makes onSubmit send a PUT, so a
  // form that looks like a new contact silently overwrites the last edited one.
  const handleAddContact = () => {
    setEditingContact(null);
    form.reset(emptyContactForm);
    setIsDialogOpen(true);
  };

  const openCompanyDialog = (target: CompanyTarget, membersToAdopt: Contact[] = []) => {
    setCompanyTarget(target);
    setAdoptedMembers(membersToAdopt);
  };

  // A GA with no id is being created, so it starts from the adopted rows.
  const companyMembers = !companyTarget
    ? []
    : companyTarget.kind === "carrier"
      ? contacts.filter((contact) => contact.carrierId === companyTarget.carrier.id)
      : companyTarget.generalAgency
        ? contacts.filter(
            (contact) => contact.generalAgencyId === companyTarget.generalAgency!.id,
          )
        : adoptedMembers;

  const handleEdit = (contact: Contact) => {
    // A GA is edited as a firm, from any tab.
    if (contact.type === "ga") {
      const firm = generalAgencies.find(
        (generalAgency) => generalAgency.id === contact.generalAgencyId,
      ) ?? null;
      // No firm means its firm was deleted (ON DELETE SET NULL keeps the
      // person). Carry them into a new firm so saving reattaches, not duplicates.
      openCompanyDialog({ kind: "ga", generalAgency: firm }, firm ? [] : [contact]);
      return;
    }
    setEditingContact(contact);
    const addressDefaults = normalizeAddressFields(contact as any);
    form.reset({
      name: contact.name || "",
      contactName: contact.contactName || "",
      email: contact.email || "",
      phone: contact.phone || "",
      website: contact.website || "",
      address: contact.address || "",
      ...addressDefaults,
      description: contact.description || "",
      category: contact.category || "",
      type: contact.type,
      contactType: contact.contactType || "sales",
      carrierId: contact.carrierId || undefined,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this contact?")) {
      deleteMutation.mutate(id);
    }
  };

  const exportSalesContactsForRFP = () => {
    const salesContacts = contacts.filter(
      (contact) => contact.contactType === "sales"
    );

    const rfpData = salesContacts.map((contact) => ({
      Company: contact.name,
      ContactName: contact.contactName || "N/A",
      Email: contact.email,
      Phone: contact.phone || "N/A",
      Type: CONTACT_TYPE_LABELS[contact.type] ?? "Service Vendor",
      Category: contact.category || "N/A",
    }));

    exportToCSV(rfpData, {
      filename: `rfp-sales-contacts-${new Date().toISOString().split("T")[0]}`,
      headers: ["Company", "ContactName", "Email", "Phone", "Type", "Category"],
      toast: {
        success: "Sales contacts exported successfully for RFP",
        error: "No sales contacts to export",
      },
    });
  };

  const onSubmit = (data: any) => {
    const addressLine = composeAddressLine(data);
    const payload = {
      ...data,
      address: addressLine || data.address || "",
      address1: data.address1 || null,
      address2: data.address2 || null,
      city: data.city || null,
      state: data.state || null,
      zipCode: data.zipCode || null,
      county: data.county || null,
      country: data.country || null,
      carrierId: data.carrierId || null,
    };
    if (editingContact) {
      updateMutation.mutate({ id: editingContact.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Insurance Directory</h1>
          <p className="text-muted-foreground">
            Manage insurance carriers and vendor contacts
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="dialog-description">
            <DialogHeader>
              <DialogTitle>
                {editingContact
                  ? "Edit Contact"
                  : isGeneralAgencyMode
                    ? "Add General Agency"
                    : "Add New Contact"}
              </DialogTitle>
              <p id="dialog-description" className="text-sm text-muted-foreground">
                {editingContact
                  ? "Update the contact information below."
                  : isGeneralAgencyMode
                    ? "An RFP goes to every sales contact listed here."
                    : "Add a new insurance carrier or vendor contact to your directory."}
              </p>
            </DialogHeader>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="carrier">
                            Insurance Carrier
                          </SelectItem>
                          <SelectItem value="vendor">Service Vendor</SelectItem>
                          {/* Picking this swaps the body below for the firm
                              form: a GA is a company with several sales
                              contacts, not a single person. */}
                          <SelectItem value="ga">General Agent (GA)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {isGeneralAgencyMode ? (
                  <CompanyContactsFields form={generalAgencyForm} />
                ) : (
                  <>
                {form.watch("type") === "carrier" ? (
                  <FormField
                    control={form.control}
                    name="carrierId"
                    render={({ field }) => {
                      const sortedCarriers = carriers
                        .filter((c: any) => c.isActive)
                        .sort((a: any, b: any) => a.name.localeCompare(b.name));
                      const selectedCarrier = carriers.find((c: any) => c.id === field.value);

                      return (
                        <FormItem className="flex flex-col">
                          <FormLabel>Carrier <span className="text-red-500">*</span></FormLabel>
                          <Popover open={carrierPopoverOpen} onOpenChange={setCarrierPopoverOpen}>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  aria-expanded={carrierPopoverOpen}
                                  className={cn(
                                    "w-full justify-between",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {selectedCarrier
                                    ? `${selectedCarrier.name}${selectedCarrier.types ? ` (${selectedCarrier.types.join(', ')})` : ''}`
                                    : "Select a carrier"}
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" onWheel={(e) => e.stopPropagation()}>
                              <Command>
                                <CommandInput placeholder="Search carriers..." />
                                <CommandEmpty>No carrier found.</CommandEmpty>
                                <CommandList style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                  <CommandGroup>
                                    {sortedCarriers.map((carrier: any) => (
                                      <CommandItem
                                        key={carrier.id}
                                        value={`${carrier.name} ${carrier.types?.join(' ') || ''}`}
                                        onSelect={() => {
                                          field.onChange(carrier.id);
                                          form.setValue("name", carrier.name);
                                          setCarrierPopoverOpen(false);
                                        }}
                                      >
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4",
                                            field.value === carrier.id ? "opacity-100" : "opacity-0"
                                          )}
                                        />
                                        {carrier.name} {carrier.types && `(${carrier.types.join(', ')})`}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                ) : (
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter company name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                <FormField
                  control={form.control}
                  name="contactName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Person</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter contact person name"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="email"
                    // No zodResolver on this form, so per-field `rules` is the
                    // only validation. RFP recipients are picked by email, so a
                    // contact without one is silently skipped.
                    rules={{
                      required: "An email address is required",
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email address" },
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email <span className="text-red-500">*</span></FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="Enter email"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => {
                      const handlePhoneChange = (
                        event: ChangeEvent<HTMLInputElement>
                      ) => {
                        const formattedValue = formatPhoneInputValue(
                          event.target.value
                        );

                        field.onChange(formattedValue);
                      };

                      return (
                        <FormItem>
                          <FormLabel>Phone</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value || ""}
                              placeholder="Enter phone number"
                              onChange={handlePhoneChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Website</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter website URL" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter category" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="contactType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select contact type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="sales">Sales Contact</SelectItem>
                          <SelectItem value="service">
                            Service Contact
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <AddressFields control={form.control} />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Enter description" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                  </>
                )}
                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  {/* Not react-hook-form's submit: the firm form keeps its own
                      state, and that path would POST a contact with no name. */}
                  {isGeneralAgencyMode ? (
                    <Button
                      type="button"
                      onClick={generalAgencyForm.submit}
                      disabled={generalAgencyForm.isSaving}
                    >
                      {generalAgencyForm.isSaving && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Add General Agency
                    </Button>
                  ) : (
                    <Button type="submit">
                      {editingContact ? "Update Contact" : "Add Contact"}
                    </Button>
                  )}
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPI tiles, same shape as Reports. Carriers and GAs count companies,
          the other two count people, so each says which. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryTile
          icon={Users}
          tone="blue"
          label="Total Contacts"
          value={totalContacts}
          hint="People in your directory"
        />
        <SummaryTile
          icon={Building2}
          tone="purple"
          label="Insurance Carriers"
          value={carriersWithContacts.length}
          hint="With a contact on file"
        />
        <SummaryTile
          icon={Handshake}
          tone="cyan"
          label="General Agencies"
          value={gaCount}
          hint="Wholesalers for your RFPs"
        />
        <SummaryTile
          icon={Briefcase}
          tone="orange"
          label="Service Vendors"
          value={vendorCount}
          hint="Vendor contacts"
        />
      </div>

      {/* Search and Filter */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contacts by name, email, or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
        <Button
          variant="outline"
          onClick={exportSalesContactsForRFP}
          disabled={
            contacts.filter((c: Contact) => c.contactType === "sales")
              .length === 0
          }
        >
          <Download className="mr-2 h-4 w-4" />
          Export for RFP
        </Button>
        <PermissionButton
          permission="directory:create"
          whenDenied="hide"
          onClick={handleAddContact}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Contact
        </PermissionButton>
      </div>

      {/* Directory Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6 bg-[#eff6fe] dark:bg-gray-800">
          <TabsTrigger value="all" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">All Contacts ({totalContacts})</TabsTrigger>
          <TabsTrigger value="carrier" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Carriers ({carriersWithContacts.length})</TabsTrigger>
          <TabsTrigger value="ga" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">GAs ({gaCount})</TabsTrigger>
          <TabsTrigger value="vendor" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Vendors ({vendorCount})</TabsTrigger>
          <TabsTrigger value="sales" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">
            Sales Contacts (
            {contacts.filter((c: Contact) => c.contactType === "sales").length})
          </TabsTrigger>
          <TabsTrigger value="service" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">
            Service Contacts (
            {
              contacts.filter((c: Contact) => c.contactType === "service")
                .length
            }
            )
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {isLoading ? (
            <div>Loading...</div>
          ) : (
            <ContactGrid
              contacts={filteredContacts}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          )}
        </TabsContent>

        <TabsContent value="carrier" className="space-y-6">
          {/* Grouped like the GAs tab: an RFP goes to every sales contact of
              each carrier picked, so two people under one carrier matters. */}
          <ContactGroups
            companies={carriersWithContacts}
            contacts={filteredContacts}
            linkField="carrierId"
            companyIcon={Building2}
            isSearching={searchTerm.trim().length > 0}
            showEmptyCompanies={false}
            emptyState={{
              title: "No carrier contacts yet",
              hint: 'Use "Add Contact" and pick Insurance Carrier to add one.',
            }}
            emptyCompanyHint="Nobody added yet."
            unlinkedHint="Edit each one and pick its carrier so RFPs reach them."
            onEditCompany={(company) => openCompanyDialog({ kind: "carrier", carrier: company })}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </TabsContent>

        <TabsContent value="ga" className="space-y-6">
          <ContactGroups
            companies={generalAgencies.filter((ga) => ga.isActive !== false)}
            contacts={filteredContacts}
            linkField="generalAgencyId"
            companyIcon={Handshake}
            isSearching={searchTerm.trim().length > 0}
            showEmptyCompanies
            emptyState={{
              title: "No general agencies yet",
              hint: 'Use "Add Contact" and pick General Agent (GA) to add one, along with everyone who should receive your RFPs.',
            }}
            emptyCompanyHint="Nobody added yet. An RFP cannot be sent to this GA until it has a sales contact."
            unlinkedHint="Edit each one and pick its general agency so RFPs reach them."
            onEditCompany={(company) =>
              openCompanyDialog({
                kind: "ga",
                generalAgency: generalAgencies.find((ga) => ga.id === company.id) ?? null,
              })
            }
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </TabsContent>

        <TabsContent value="vendor" className="space-y-4">
          <ContactGrid
            contacts={filteredContacts}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </TabsContent>

        <TabsContent value="sales" className="space-y-4">
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700">
              Sales contacts are the primary contacts for RFP processes and
              quote requests. Use the "Export for RFP" button to download
              contact information for your quoting workflows.
            </p>
          </div>
          <ContactGrid
            contacts={filteredContacts}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </TabsContent>

        <TabsContent value="service" className="space-y-4">
          <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-green-700">
              Service contacts handle customer support, claims processing, and
              ongoing account management.
            </p>
          </div>
          <ContactGrid
            contacts={filteredContacts}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </TabsContent>
      </Tabs>

      <CompanyContactsDialog
        open={companyTarget !== null}
        onOpenChange={(open) => !open && setCompanyTarget(null)}
        target={companyTarget}
        members={companyMembers}
        allContacts={contacts}
      />
    </div>
  );
}

// Tailwind needs whole class names in the source, so the tones are spelled out
// rather than built from the prop.
const SUMMARY_TONES = {
  blue: "bg-blue-100 dark:bg-blue-900 text-blue-600",
  purple: "bg-purple-100 dark:bg-purple-900 text-purple-600",
  cyan: "bg-cyan-100 dark:bg-cyan-900 text-cyan-600",
  orange: "bg-orange-100 dark:bg-orange-900 text-orange-600",
} as const;

function SummaryTile({
  icon: Icon,
  tone,
  label,
  value,
  hint,
}: {
  icon: typeof Users;
  tone: keyof typeof SUMMARY_TONES;
  label: string;
  value: number;
  hint: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={cn("p-2 rounded-lg shrink-0", SUMMARY_TONES[tone])}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground truncate">{label}</p>
            <p className="text-2xl font-bold leading-tight">{value}</p>
            <p className="text-xs text-muted-foreground truncate">{hint}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** A carrier or a general agency: the company a group of contacts belongs to. */
interface ContactCompany {
  id: number;
  name: string;
  /** GAs only: how many carriers it can quote. Undefined for carriers. */
  carrierIds?: number[];
}

// One block per company, its people inside, for the Carriers and GAs tabs.
// ContactGrid is reused per group rather than changed: it serves six tabs and
// only these two group by company.
function ContactGroups({
  companies,
  contacts,
  linkField,
  companyIcon: CompanyIcon,
  isSearching,
  showEmptyCompanies,
  emptyState,
  emptyCompanyHint,
  unlinkedHint,
  onEditCompany,
  onEdit,
  onDelete,
}: {
  companies: ContactCompany[];
  contacts: Contact[];
  companyIcon: typeof Users;
  /** Which column ties a contact to its company. */
  linkField: "carrierId" | "generalAgencyId";
  isSearching: boolean;
  /** GAs show when empty (the broker's own list); carriers do not (40+ shared). */
  showEmptyCompanies: boolean;
  emptyState: { title: string; hint: string };
  emptyCompanyHint: string;
  unlinkedHint: string;
  onEditCompany: (company: ContactCompany) => void;
  onEdit: (contact: Contact) => void;
  onDelete: (id: number) => void;
}) {
  const sortedCompanies = [...companies].sort((a, b) => a.name.localeCompare(b.name));

  // No company: predates the link, or the company was deleted (ON DELETE SET
  // NULL keeps the person). Shown last so they are not lost.
  const unlinked = contacts.filter((contact) => !contact[linkField]);

  const groups = sortedCompanies
    .map((company) => ({
      company,
      members: contacts.filter((contact) => contact[linkField] === company.id),
    }))
    // Searching filters contacts in the parent, so a company whose own name
    // matches but whose people do not would otherwise render an empty block.
    .filter((group) => (showEmptyCompanies && !isSearching) || group.members.length > 0);

  if (groups.length === 0 && unlinked.length === 0) {
    return (
      <div className="text-center py-8">
        <Building2 className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{emptyState.title}</h3>
        <p className="mt-1 text-sm text-gray-500">{emptyState.hint}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map(({ company, members }) => {
        const salesCount = members.filter((m) => m.contactType === "sales").length;
        return (
          <Card key={company.id} className="overflow-hidden">
            <div className="flex items-center gap-3 border-b bg-muted/40 px-4 py-3">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <CompanyIcon className="h-4 w-4 text-primary" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="truncate font-semibold leading-tight">{company.name}</h3>
                  <Badge variant="secondary" className="gap-1 font-normal shrink-0">
                    <Users className="h-3 w-3" />
                    {members.length} {members.length === 1 ? "contact" : "contacts"}
                  </Badge>
                  {/* GAs only. An empty list means nothing can be sent through
                      this GA at all, so it is loud rather than a muted note. */}
                  {company.carrierIds &&
                    (company.carrierIds.length === 0 ? (
                      <Badge
                        variant="outline"
                        className="gap-1 font-medium shrink-0 border-amber-400 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-400"
                      >
                        <AlertCircle className="h-3 w-3" />
                        No carriers linked
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1 font-normal shrink-0">
                        <Building2 className="h-3 w-3" />
                        {company.carrierIds.length}{" "}
                        {company.carrierIds.length === 1 ? "carrier" : "carriers"}
                      </Badge>
                    ))}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {members.length === 0
                    ? "Nobody to send to yet"
                    : `${salesCount} of them ${salesCount === 1 ? "receives" : "receive"} the RFP`}
                </p>
              </div>
              {/* The way in for adding a second person: company and contacts are
                  edited together, so there is no per-contact add. */}
              <PermissionButton
                permission="directory:edit"
                whenDenied="hide"
                variant="outline"
                size="sm"
                onClick={() => onEditCompany(company)}
              >
                <Edit2 className="h-3 w-3 mr-1.5" />
                Edit contacts
              </PermissionButton>
            </div>
            {members.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                {emptyCompanyHint}
              </p>
            ) : (
              <div className="divide-y">
                {members.map((contact) => (
                  <CompanyContactRow
                    key={contact.id}
                    contact={contact}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                ))}
              </div>
            )}
          </Card>
        );
      })}

      {unlinked.length > 0 && (
        <Card className="overflow-hidden border-amber-300 dark:border-amber-800">
          <div className="border-b bg-amber-50 px-4 py-3 dark:bg-amber-950/40">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-500" />
              <h3 className="font-semibold">Not linked</h3>
              <Badge variant="outline" className="text-xs">{unlinked.length}</Badge>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">{unlinkedHint}</p>
          </div>
          <div className="divide-y">
            {unlinked.map((contact) => (
              <CompanyContactRow
                key={contact.id}
                contact={contact}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/** Initials for the avatar, falling back to the email when there is no name. */
function contactInitials(contact: Contact) {
  const source = contact.contactName?.trim() || contact.email || "?";
  return source
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

// A contact inside a company card. Denser than ContactGrid's card: the company
// name is in the header already, so the row never repeats it.
function CompanyContactRow({
  contact,
  onEdit,
  onDelete,
}: {
  contact: Contact;
  onEdit: (contact: Contact) => void;
  onDelete: (id: number) => void;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/30">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
        {contactInitials(contact)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">
            {contact.contactName || "No name yet"}
          </span>
          {/* Sales gets the accent: it decides who an RFP reaches. */}
          <Badge
            variant={contact.contactType === "sales" ? "default" : "secondary"}
            className="text-[10px] px-1.5 py-0 shrink-0"
          >
            {contact.contactType === "sales" ? "Sales" : "Service"}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <span className="inline-flex min-w-0 items-center gap-1">
            <Mail className="h-3 w-3 shrink-0" />
            <span className="truncate">{contact.email}</span>
          </span>
          {contact.phone && (
            <span className="inline-flex items-center gap-1">
              <Phone className="h-3 w-3 shrink-0" />
              {contact.phone}
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0">
        <PermissionButton
          permission="directory:edit"
          whenDenied="hide"
          variant="ghost"
          size="sm"
          onClick={() => onEdit(contact)}
        >
          <Edit2 className="h-3 w-3" />
        </PermissionButton>
        <PermissionButton
          permission="directory:delete"
          whenDenied="hide"
          variant="ghost"
          size="sm"
          onClick={() => onDelete(contact.id)}
        >
          <Trash2 className="h-3 w-3" />
        </PermissionButton>
      </div>
    </div>
  );
}

function ContactGrid({
  contacts, 
  onEdit, 
  onDelete 
}: { 
  contacts: Contact[]; 
  onEdit: (contact: Contact) => void;
  onDelete: (id: number) => void;
}) {
  if (contacts.length === 0) {
    return (
      <div className="text-center py-8">
        <Building2 className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No contacts found</h3>
        <p className="mt-1 text-sm text-gray-500">
          Try adjusting your search terms or add a new contact.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {contacts.map((contact) => {
        const TypeIcon = CONTACT_TYPE_ICONS[contact.type] ?? Briefcase;
        // A carrier or GA contact with no company link: it still works, but it
        // is invisible to the grouped tabs and to the RFP.
        const isUnlinked =
          (contact.type === "carrier" && !contact.carrierId) ||
          (contact.type === "ga" && !contact.generalAgencyId);

        return (
          <Card key={contact.id} className="flex flex-col hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start gap-3">
                <span
                  className={cn(
                    "p-2 rounded-lg shrink-0",
                    CONTACT_TYPE_TONES[contact.type] ?? CONTACT_TYPE_TONES.vendor,
                  )}
                >
                  <TypeIcon className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-base leading-tight truncate">
                    {contact.name}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {CONTACT_TYPE_LABELS[contact.type] ?? "Service Vendor"}
                  </p>
                </div>
                <div className="flex shrink-0 -mr-2 -mt-1">
                  <PermissionButton
                    permission="directory:edit"
                    whenDenied="hide"
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(contact)}
                  >
                    <Edit2 className="h-3 w-3" />
                  </PermissionButton>
                  <PermissionButton
                    permission="directory:delete"
                    whenDenied="hide"
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(contact.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </PermissionButton>
                </div>
              </div>
            </CardHeader>

            <CardContent className="flex flex-1 flex-col gap-3 pt-0">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">
                  {contactInitials(contact)}
                </span>
                <span className="truncate text-sm font-medium">
                  {contact.contactName || "No name yet"}
                </span>
                {contact.contactType && (
                  <Badge
                    variant={contact.contactType === "sales" ? "default" : "secondary"}
                    className="text-[10px] px-1.5 py-0 shrink-0"
                  >
                    {contact.contactType === "sales" ? "Sales" : "Service"}
                  </Badge>
                )}
              </div>

              <div className="space-y-1.5 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate" title={contact.email}>{contact.email}</span>
                </div>
                {contact.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span>{contact.phone}</span>
                  </div>
                )}
                {contact.website && (
                  <div className="flex items-center gap-2 min-w-0">
                    <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <a
                      href={`https://${contact.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-primary hover:underline"
                    >
                      {contact.website}
                    </a>
                  </div>
                )}
                {contact.address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground" />
                    <span className="text-muted-foreground">{contact.address}</span>
                  </div>
                )}
              </div>

              {/* mt-auto, so cards line up however many optional rows they have. */}
              <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1">
                {/* The title is already the carrier's name; the lines it quotes
                    for are what the old "Carrier:" box actually added. */}
                {contact.carrier?.types?.map((coverageType) => (
                  <Badge key={coverageType} variant="outline" className="text-[10px] px-1.5 py-0">
                    {coverageType}
                  </Badge>
                ))}
                {contact.category && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    {contact.category}
                  </Badge>
                )}
                {isUnlinked && (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-500"
                  >
                    Not linked
                  </Badge>
                )}
              </div>

              {contact.description && (
                <p className="border-t pt-2 text-xs text-muted-foreground">
                  {contact.description}
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
